import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    signal,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { ModalComponent } from "../modal.component";
import { S25IconButtonComponent } from "../../../standalone/s25.icon.button.component";
import { NetworkHealthService } from "../../../services/network.health.service";
import { s25Resource } from "../../../util/s25.resource";
import { PreferenceService } from "../../../services/preference.service";
import { S25Util } from "../../../util/s25-util";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { S25Const } from "../../../util/s25-const";
import { S25PipesModule } from "../../../pipes/s25.pipes.module";
import { FormField, form, validate } from "@angular/forms/signals";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { PasswordResetService } from "../../../services/password.reset.service";
import { AuthService } from "../../../services/auth.service";
import { WelcomeMessageUtil } from "../../../services/welcome.message.util";
import { Bind } from "../../../decorators/bind.decorator";
import { ModalHeaderComponent } from "../modal.header.component";
import { AutofocusDirective } from "../../../standalone/s25.autofocus.directive";

@Component({
    standalone: true,
    selector: "s25-ng-login-modal",
    template: `
        <s25-modal-header [data]="params()" />
        <div class="modal-body">
            @if (prefs.resource.isLoading()) {
                <s25-ng-loading-inline-static />
            } @else if (prefs.resource.error()) {
                <div class="error">
                    <span>Unable to load Login Page. Please try again later.</span>
                    <s25-ng-icon-button
                        [type]="'refresh'"
                        (clicked)="prefs.resource.reload()"
                        [ariaLabel]="'Refresh'"
                    />
                </div>
            } @else if (!networkHealthService.healthy()) {
                <div class="error">
                    Unable to Log In due to poor network connection. Please check your connection and try again.
                </div>
            } @else {
                @if (loginMessage) {
                    <div class="login-message" [innerHTML]="loginMessage() | safeHTML"></div>
                }
                <form (keydown.enter)="signIn()">
                    <div class="form-row">
                        <label for="login-username">Username</label>
                        <input
                            s25-ng-auto-focus
                            id="login-username"
                            type="text"
                            autocomplete="on"
                            autocapitalize="off"
                            name="username"
                            class="c-input"
                            [formField]="form.username"
                        />
                    </div>
                    <div class="form-row">
                        <label for="login-password">Password</label>
                        <input
                            #passwordInput
                            id="login-password"
                            type="password"
                            name="password"
                            autocomplete="on"
                            class="c-input"
                            [formField]="form.password"
                        />
                    </div>
                    @if (loginError()) {
                        <p class="form-error">{{ loginError() }}</p>
                    }
                </form>
                @if (showForgotPassword()) {
                    <s25-ng-button
                        [type]="'link'"
                        [onClick]="forgotPassword"
                        [disabled]="!form.username().valid()"
                        [disabledReason]="form.username().errors()?.[0]?.message"
                    >
                        Forgot your Password?
                    </s25-ng-button>
                }
                @if (forgotPasswordResult()) {
                    <p class="forgot-password-result" [attr.data-success]="forgotPasswordResult().success">
                        {{ forgotPasswordResult().message }}
                    </p>
                }
                <div class="buttons">
                    @if (params()?.closable) {
                        <s25-ng-button [type]="'outline'" (clicked)="params()?.closeModal()"> Cancel </s25-ng-button>
                    }
                    <s25-ng-button
                        [type]="'primary'"
                        (clicked)="signIn()"
                        [disabled]="!form().valid()"
                        [disabledReason]="form().errorSummary()?.[0]?.message"
                        [isLoading]="signingIn()"
                    >
                        Sign In
                    </s25-ng-button>
                </div>
            }
        </div>
    `,
    styles: `
        .modal-body {
            text-align: center;
        }

        .login-message {
            font-size: 0.75em;
            color: #737373;
        }

        form {
            padding-block: 0.5rem;
            display: grid;
            row-gap: 1rem;
        }

        .form-row {
            display: flex;
            column-gap: 1rem;
            row-gap: 0.25rem;
            justify-content: center;
            align-items: center;

            label {
                width: 9ch;
            }

            input {
                width: 22ch;
            }
        }

        .form-error {
            color: var(--error-message-color);
        }

        .buttons {
            display: flex;
            gap: 0.5rem;
            justify-content: end;
            padding-block-start: 0.5rem;
        }

        @media screen and (width <= 700px) {
            .login-message {
                font-size: 1rem;
            }

            .form-row {
                flex-direction: column;
                align-items: start;
            }

            .buttons {
                justify-content: center;

                & > * {
                    flex-grow: 1;
                }
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25IconButtonComponent,
        S25LoadingInlineStaticComponent,
        S25PipesModule,
        FormField,
        S25ButtonComponent,
        ModalHeaderComponent,
        AutofocusDirective,
    ],
})
export class LoginModalComponent extends ModalComponent {
    protected readonly networkHealthService = NetworkHealthService;
    protected readonly preferenceService = PreferenceService;
    protected readonly passwordResetService = PasswordResetService;
    protected readonly S25Util = S25Util;
    protected readonly S25Const = S25Const;
    protected readonly authService = AuthService;
    protected readonly welcomeMessageUtil = WelcomeMessageUtil;
    private static PREFERENCES = ["config_login_msg", "config_email_password"];

    readonly prefs = s25Resource({
        loader: () => this.preferenceService.getPreferences(LoginModalComponent.PREFERENCES),
    });
    readonly loginMessage = computed(() => {
        const prefs = this.prefs.value();
        let message = <string | undefined>prefs?.config_login_msg?.value;
        if (message?.[0] === "@") message = message.substring(1);
        if (!message) return;
        return this.S25Util.unescapeXml(message);
    });
    readonly showForgotPassword = computed(() => this.prefs.value()?.config_email_password?.value === "show");

    readonly userModel = signal<{ username: string; password: string }>({ username: "", password: "" });
    readonly form = form(this.userModel, (schema) => {
        validate(schema.username, (field) => {
            if (!field.value()) return { kind: "empty-username", message: "Enter a username" };
        });
        validate(schema.password, (field) => {
            if (!field.value()) return { kind: "empty-password", message: "Enter a password" };
        });
    });

    readonly signingIn = signal(false);
    readonly loginError = signal<string | undefined>(undefined);
    readonly forgotPasswordResult = signal<{ success: boolean; message: string } | undefined>(undefined);
    readonly passwordElement = viewChild<ElementRef<HTMLInputElement>>("passwordInput");

    constructor() {
        super();
        if (this.S25Const.inApp) this.networkHealthService.redisplayBanner();

        effect(() => {
            // Invalidate preferences if they error
            const error = this.prefs.resource.error();
            if (error) this.preferenceService.invalidatePreferences(LoginModalComponent.PREFERENCES);
        });
    }

    async signIn() {
        if (!this.form().valid() || this.signingIn()) return;

        this.signingIn.set(true);
        this.loginError.set(undefined);
        this.forgotPasswordResult.set(undefined);
        const { username, password } = this.form().value();

        const [isLoggedIn, err] = await this.S25Util.Maybe(this.authService.authenticate(username, password));
        this.signingIn.set(false);
        if (isLoggedIn) {
            this.params()?.closeModal();
            await this.welcomeMessageUtil.autoShowWelcomeMessage();
        } else {
            const locked = err && this.S25Util.prettyConv(err.error)?.results?.error?.msg_id === "ACCT_LOCKED";
            if (locked) this.loginError.set("The account is locked. Please try again later.");
            else {
                this.loginError.set("Invalid user name or password.");
                this.passwordElement()?.nativeElement.focus();
            }
        }
    }

    @Bind
    async forgotPassword() {
        if (!this.form.username().valid()) return;

        this.loginError.set(undefined);
        const username = this.form.username().value();

        this.forgotPasswordResult.set(await this.passwordResetService.requestReset(username));
    }
}
